/**
 * Group-member dropdown in the property strip (design §10). Selecting a
 * member NAVIGATES to that sibling application's own v2 URL — v2 loads one
 * application deeply, it never re-implements v1's all-in-one tabs.
 * Solo applications (groupMembers is [] by contract) get the plain name —
 * design §3 always shows it next to the screening-kind pill.
 *
 * 2026-09-09 (client): each row is the DASHBOARD member chip (MemberChipBody
 * — ringed avatar + name, the "Status Circles" legend via
 * deriveMemberChipVisual) with the All Applications role pill
 * (deriveMemberRolePillClass: applicants green, guarantors amber, invited
 * muted) — one shared legend, never a per-surface fork. The label is the
 * real relationship (invitation-first, derive order ①-④), and
 * invited-but-not-joined members render as muted non-navigable rows so the
 * household composition is visible here like on the listing chips, the
 * combined tiles and the decision dialog. (Supersedes the 2026-07-13
 * status-toned PastelChip rows.)
 */
import { ChevronDown } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router'
import { MemberChipBody } from '#app/components/application-detail-v2/vendor/MemberChip'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { dedupePendingInvitations } from '#app/utils/application-detail-v2/compose'
import { preserveReturnTo } from '#app/utils/application-detail-v2/return-to'
import type {
	GroupMember,
	PendingInvitationSummary,
} from '#app/utils/application-detail-v2/types'
import {
	deriveMemberRolePillClass,
	deriveShortMemberName,
} from '#app/utils/application-detail-v2/vendor/card-kit'
import { cn } from '#app/utils/misc'
import { chipInput, memberInitials } from './member-chip-input'

/** The All Applications role pill (AllApplicantsSheet rows) — same classes. */
function RolePill({
	kind,
	label,
}: {
	kind: Parameters<typeof deriveMemberRolePillClass>[0]
	label: string
}) {
	return (
		<span
			className={cn(
				'shrink-0 rounded-full px-4 py-1.5 text-sm font-medium',
				deriveMemberRolePillClass(kind),
			)}
		>
			{label}
		</span>
	)
}

export function ApplicantSwitcher({
	groupMembers,
	propertyId,
	currentName,
	currentApplicationId,
	pendingInvitations = [],
}: {
	groupMembers: GroupMember[]
	propertyId: string
	currentName: string
	/** The member whose report is on screen — highlighted, not navigable. */
	currentApplicationId?: string
	/** Invited-but-not-joined members — muted rows, no navigation target. */
	pendingInvitations?: PendingInvitationSummary[]
}) {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	// Re-sent invites arrive as one row per send — show each invitee once.
	const invitations = dedupePendingInvitations(pendingInvitations)
	if (groupMembers.length === 0 && invitations.length === 0) {
		// Nothing to switch — just the name, no chevron, no menu.
		return (
			<span className="text-sm font-medium text-[#32302F]">{currentName}</span>
		)
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="group flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[#32302F]">
				{currentName}
				<ChevronDown className="size-4 text-[#32302F] transition-transform duration-200 group-data-[state=open]:rotate-180" />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={10}
				className="w-[360px] rounded-3xl bg-white p-5 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)]"
			>
				{groupMembers.map((member) => {
					const isCurrent = member.applicationId === currentApplicationId
					return (
						<DropdownMenuItem
							key={member.applicationId}
							aria-current={isCurrent ? 'true' : undefined}
							className={cn(
								'flex cursor-pointer items-center justify-between gap-6 rounded-xl px-2 py-2 focus:bg-[#F6F7F5]',
							)}
							onSelect={() => {
								if (isCurrent) return
								// Carry the origin forward so switching applicants never
								// changes where "Back To Applications" lands (return-to.ts).
								navigate(
									preserveReturnTo(
										`/properties/${propertyId}/applications/${member.applicationId}/v2`,
										searchParams,
									),
								)
							}}
						>
							{/* The dashboard chip body — ring + badge from the shared legend,
							    and the listing's short name ("John S": first name + last
							    initial, deriveShortMemberName). */}
							<MemberChipBody
								member={{
									...chipInput(member),
									shortName: deriveShortMemberName(member.displayName),
								}}
								size="lg"
							/>
							<RolePill
								kind={chipInput(member).kind}
								label={member.relationshipLabel}
							/>
						</DropdownMenuItem>
					)
				})}
				{invitations.map((invitation) => (
					<DropdownMenuItem
						key={invitation.id}
						disabled
						// Muted + non-navigable: there is no application page yet — the
						// row exists so the full household is visible from the header.
						className="flex items-center justify-between gap-6 rounded-xl px-2 py-2 opacity-100 data-[disabled]:pointer-events-none"
					>
						{(() => {
							const label =
								invitation.invitedName?.trim() ||
								invitation.invitedEmail ||
								'Invited member'
							// Listing rule (card-assembly): a NAMED invite shortens like a
							// member; an email-only invite shows the email as-is.
							const shortName = invitation.invitedName?.trim()
								? deriveShortMemberName(label)
								: label
							return (
								<>
									{/* The dashboard's invited chip — gray ring, initials. */}
									<MemberChipBody
										member={{
											kind: 'invited',
											status: 'invited',
											hasStarted: false,
											startedAt: null,
											infoRequested: false,
											billingHeld: false,
											initials: memberInitials(label),
											shortName,
										}}
										size="lg"
									/>
									<RolePill
										kind="invited"
										label={
											invitation.relationship ||
											(invitation.invitationType === 'guarantor'
												? 'Guarantor'
												: 'Invited')
										}
									/>
								</>
							)
						})()}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
